import { Entity, StoreState } from '@degreed/lxp/services';
import { Observable } from 'rxjs';

import { Filter } from '@app/shared/components/filter/filter.component';
import {
  GetOrgTargetResponse,
  OrgTargetVM,
} from '@app/target/target-api.model';
import { OrgPlansColumnIndex } from '../enums/org-plans.enums';

export type OrgPaging = {
  take: number;
  totalCount: number;
  page: number;
};
export type OrgOrdering = {
  orderBy: OrgPlansColumnIndex;
  isDescending: boolean;
};

export type LoadOptions = {
  ordering: Partial<OrgOrdering>;
  paging: Partial<OrgPaging>;
  term: string;
  filterType?: number[];
  clearPlans?: boolean;
};

export type OrgPlansSource = (
  params: LoadOptions
) => Observable<GetOrgTargetResponse>;

export type OrgPlanStateWithEntities = OrgPlansState & {
  ids: string[];
  entities: Record<string, OrgPlanEntity>;
};

export type OrgPlansComputedState = {
  allPlans: OrgPlanEntity[];
  selectedPlans: OrgPlanEntity[];
  /**
   * Whether EVERY currently selected plan is individually deletable — the
   * per-item half of the bulk-delete gate (PD-130899).
   *
   * The page-level `canDeletePlans` roll-up decides whether the bulk toolbar
   * and row checkboxes render at all; this decides whether the Delete button is
   * *enabled* for the current selection. Derived here rather than cached on the
   * component so it cannot go stale: every selection mutation funnels through
   * `OrgPlansFacade.selectPlans`.
   */
  canBulkDeleteSelected: boolean;
};

export type OrgPlansViewModel = OrgPlansState &
  OrgPlansAPI &
  OrgPlansComputedState;

export interface OrgPlansAPI {
  filters: Filter[];
  updateSort: (column: string) => void;
  updateFilters: (filters: Filter[]) => void;
  search: (term: string) => void;
  changePage: (page: number) => void;
  changePageSize: (size: number) => void;
}

export function initState(): OrgPlansState {
  return {
    term: '',
    paging: {
      take: 10,
      totalCount: undefined,
      page: 1,
    },
    ordering: {
      orderBy: OrgPlansColumnIndex.name,
      isDescending: false,
    },
    filterType: [],
    isSorting: false,
  };
}

export type OrgPlanEntity = OrgTargetVM & Entity;

export interface OrgPlansState extends StoreState {
  term: string;
  paging: OrgPaging;
  ordering: OrgOrdering;
  filterType: number[];
  isSorting: boolean;
}
